import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Truck, ShieldCheck } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { CATEGORIES, PRODUCTS } from "../data/products";
import ProductCard from "../components/ProductCard";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_246bdc4f-04a8-499f-a35e-a2d34f82e829/artifacts/6u8mqppy_green-light-rays-background.jpg";
const LOGO =
  "https://customer-assets.emergentagent.com/job_246bdc4f-04a8-499f-a35e-a2d34f82e829/artifacts/etd6qiby_46448324-8a4b-431b-a4f6-49b603bebb50.png";

export default function Home() {
  const { t, lang } = useI18n();
  // Met le Caducée en tête des featured pour visibilité maximale, puis 5 autres.
  const caduceeProduct = PRODUCTS.find((p) => p.slug === "caducee");
  const otherFeatured = PRODUCTS.filter((p) => p.slug !== "caducee").slice(0, 5);
  const featured = caduceeProduct ? [caduceeProduct, ...otherFeatured] : PRODUCTS.slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Gradient overlays for legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left: logo on pure black, no green glow */}
          <div className="relative z-10 flex justify-center lg:justify-start order-1">
            <img
              src={LOGO}
              alt="Signapharma"
              className="relative w-[520px] max-w-full"
              data-testid="hero-logo"
            />
          </div>

          {/* Right: content */}
          <div className="relative z-10 order-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#39FF14] neon-glow-sm" />
              <span className="label-tech text-[#39FF14]">{t.hero.eyebrow}</span>
            </div>

            <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
              <span className="block chrome-text">{t.hero.title_line1}</span>
              <span
                className="block neon-text mt-2 animate-flicker"
                data-testid="hero-title-neon"
              >
                {t.hero.title_line2}
              </span>
            </h1>

            <p className="mt-8 text-base sm:text-lg text-zinc-300 font-body leading-relaxed max-w-xl">
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/produits" className="btn-neon" data-testid="hero-cta-primary">
                {t.hero.cta_primary}
                <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-ghost-neon" data-testid="hero-cta-secondary">
                {t.hero.cta_secondary}
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-8 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <span>Saint-Jean-de-Gonville · FR</span>
              <span className="w-px h-4 bg-zinc-800" />
              <span>Est. 2026</span>
              <span className="w-px h-4 bg-zinc-800" />
              <span className="text-[#39FF14]">LED · Néon · Inox</span>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <span className="label-tech">{t.categories_section.label}</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">
              {t.categories_section.title}
            </h2>
            <p className="mt-5 text-zinc-400 font-body text-base sm:text-lg leading-relaxed">
              {t.categories_section.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {CATEGORIES.map((cat, i) => (
              <Link
                key={cat.slug}
                to={`/produits/${cat.slug}`}
                data-testid={`home-category-${cat.slug}`}
                className="group relative aspect-[4/3] md:aspect-[16/10] overflow-hidden border border-white/5 hover:border-[#39FF14]/60 transition-all"
              >
                <img
                  src={cat.image}
                  alt={t.cat[cat.key].name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10">
                  <div className="label-tech mb-2">0{i + 1}</div>
                  <h3 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white group-hover:text-[#39FF14] group-hover:neon-text-soft transition-all">
                    {t.cat[cat.key].name}
                  </h3>
                  <p className="mt-3 text-zinc-300 font-body max-w-md">
                    {t.cat[cat.key].desc}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 font-display uppercase tracking-widest text-sm font-bold text-[#39FF14]">
                    {t.products.view}
                    <ArrowRight
                      size={16}
                      className="transition-transform group-hover:translate-x-2"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CADUCÉE SHOWCASE — nouveau produit phare mis en avant */}
      <section className="relative py-20 lg:py-28 bg-[#080808] border-y border-white/5 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image rétro-éclairée */}
          <div className="relative aspect-square max-w-[560px] mx-auto lg:mx-0 w-full border border-white/5 bg-black overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/62tsgo8q_3638ed36-cb2e-43a1-a500-614908341667.png"
              alt="Caducée pharmacie lumineux"
              className="w-full h-full object-cover"
              data-testid="caducee-showcase-image"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(ellipse at center, rgba(255,209,138,0.18), transparent 65%)" }}
            />
            <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-[#FFD18A] border border-[#FFD18A]/40 bg-black/60 px-2 py-1">
              {lang === "fr" ? "Nouveau" : lang === "de" ? "Neu" : lang === "it" ? "Novità" : "New"}
            </div>
          </div>
          {/* Texte + CTA */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-[#FFD18A]" style={{ boxShadow: "0 0 12px rgba(255,209,138,0.7)" }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD18A]">
                {lang === "fr" ? "Pièce emblématique" : lang === "de" ? "Ikonisches Stück" : lang === "it" ? "Pezzo emblematico" : "Iconic piece"}
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
              <span className="chrome-text">{lang === "fr" ? "Caducée" : lang === "de" ? "Caduceus" : lang === "it" ? "Caduceo" : "Caduceus"}</span>
              <span className="block mt-2" style={{ color: "#FFD18A", textShadow: "0 0 20px rgba(255,209,138,0.6)" }}>
                {lang === "fr" ? "Pharmacie lumineux" : lang === "de" ? "Apotheke beleuchtet" : lang === "it" ? "Farmacia luminoso" : "Illuminated"}
              </span>
            </h2>
            <p className="mt-6 text-base lg:text-lg text-zinc-300 font-body leading-relaxed max-w-xl">
              {lang === "fr"
                ? "Symbole emblématique de la pharmacie française. 11 modèles découpés laser, finitions métal (gold, cuivre, inox poli ou brossé) ou peinture RAL, rétro-éclairage LED 5 couleurs. Intérieur ou extérieur (IP65)."
                : lang === "de"
                ? "Ikonisches Symbol der Frankreicher Apotheke. 11 lasergeschnittene Modelle, Metallfinish (Gold, Kupfer, Edelstahl) oder RAL-Lack, LED-Hinterleuchtung in 5 Farben. Innen oder Aussen (IP65)."
                : lang === "it"
                ? "Simbolo emblematico della farmacia svizzera. 11 modelli tagliati al laser, finiture metallo o vernice RAL, retroilluminazione LED 5 colori. Interno o esterno (IP65)."
                : "The iconic Swiss pharmacy symbol. 11 laser-cut models, metal finishes (gold, copper, polished or brushed stainless) or RAL paint, LED back-light in 5 colors. Indoor or outdoor (IP65)."}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              <span>50 – 80 cm</span>
              <span className="w-px h-4 bg-zinc-800" />
              <span className="text-[#FFD18A]">{lang === "fr" ? "Dès" : lang === "de" ? "Ab" : lang === "it" ? "Da" : "From"} 1 000 € HT</span>
              <span className="w-px h-4 bg-zinc-800" />
              <span>IP65</span>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/produits/enseigne-pharmacie/caducee"
                className="btn-neon"
                data-testid="caducee-showcase-cta"
                style={{ borderColor: "#FFD18A", color: "#FFD18A", boxShadow: "0 0 18px rgba(255,209,138,0.35), 0 0 36px rgba(255,209,138,0.15) inset" }}
              >
                {lang === "fr" ? "Configurer mon caducée" : lang === "de" ? "Caduceus konfigurieren" : lang === "it" ? "Configura il caduceo" : "Configure my caduceus"}
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/produits"
                className="btn-ghost-neon"
                data-testid="caducee-showcase-secondary"
              >
                {t.products.view}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative py-24 bg-[#080808] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight max-w-2xl">
            {t.home_features.title}
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: <Zap size={22} />, t: t.home_features.f1_title, d: t.home_features.f1_desc },
              { icon: <Truck size={22} />, t: t.home_features.f2_title, d: t.home_features.f2_desc },
              {
                icon: <ShieldCheck size={22} />,
                t: t.home_features.f3_title,
                d: t.home_features.f3_desc,
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-[#0b0b0b] border border-white/5 p-8 hover:border-[#39FF14]/40 transition-colors"
                data-testid={`feature-${i}`}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 border border-[#39FF14]/30 text-[#39FF14] neon-glow-sm mb-5">
                  {f.icon}
                </div>
                <h3 className="font-display text-xl font-semibold uppercase tracking-wide">
                  {f.t}
                </h3>
                <p className="mt-3 text-zinc-400 font-body text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="label-tech">{t.products.title}</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">
                {t.products.subtitle}
              </h2>
            </div>
            <Link
              to="/produits"
              className="hidden md:inline-flex items-center gap-2 font-display uppercase tracking-widest text-sm font-bold text-[#39FF14] hover:neon-text-soft transition-all"
              data-testid="featured-view-all"
            >
              {t.products.view} →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* SEO — Bloc texte riche en mots-clés (Saint-Jean-de-Gonville / France) */}
      <section className="relative py-20 bg-[#080808] border-y border-white/5" aria-labelledby="seo-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <span className="label-tech text-[#39FF14]">Signapharma · France</span>
            <h2 id="seo-heading" className="mt-3 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              <span className="chrome-text">Enseignes lumineuses</span>
              <br />
              <span className="neon-text-soft">pour pharmacies</span>
            </h2>
            <p className="mt-5 text-zinc-400 font-body text-sm leading-relaxed">
              Fabricant français spécialisé dans la signalétique pharmaceutique haut de gamme. Atelier à Saint-Jean-de-Gonville (Ain),
              livraison et pose dans toute la France.
            </p>
          </div>

          <div className="lg:col-span-2 space-y-6 text-zinc-300 font-body text-sm leading-relaxed">
            <p>
              <strong className="text-white">Signapharma France</strong> conçoit, personnalise, fabrique et installe des{" "}
              <strong className="text-[#39FF14]">enseignes lumineuses pour pharmacies et officines</strong> :{" "}
              <strong>croix de pharmacie LED</strong> fixes ou programmables, <strong>enseignes drapeaux double face</strong>,{" "}
              <strong>croix en néon LED</strong>, <strong>lettres boîtiers en aluminium ou inox</strong>,{" "}
              <strong>caducées lumineux</strong> et <strong>logos de pharmacie rétroéclairés</strong>. Chaque projet est
              étudié sur mesure en fonction de la façade, de la distance de visibilité, des dimensions disponibles et de
              l'identité graphique de l'officine.
            </p>
            <p>
              Depuis notre atelier situé à Saint-Jean-de-Gonville (Ain), nous intervenons prioritairement dans le{" "}
              <strong>Pays de Gex</strong>, la <strong>Haute-Savoie</strong>, la <strong>Savoie</strong>, le{" "}
              <strong>Rhône</strong>, l'<strong>Isère</strong> et la région <strong>Auvergne-Rhône-Alpes</strong>. Nous
              étudions également les projets situés à <strong>Paris</strong>, <strong>Lyon</strong>,{" "}
              <strong>Marseille</strong>, <strong>Toulouse</strong>, <strong>Nice</strong>, <strong>Bordeaux</strong>,{" "}
              <strong>Lille</strong>, <strong>Strasbourg</strong>, <strong>Nantes</strong> et partout en France
              métropolitaine, en fourniture seule, livraison ou installation selon la nature du chantier.
            </p>
            <p>
              Vous ouvrez une nouvelle officine, transférez votre pharmacie ou souhaitez moderniser une enseigne
              existante ? Notre outil de personnalisation en ligne vous accompagne dans le choix des dimensions, du
              texte, de la couleur LED (vert pharmacie RAL 6029, blanc, bicolore), de la finition (mat, brillant, brossé
              inox) et du type de pose (drapeau, façade, suspendue). Intervention et livraison partout en France selon
              la nature du projet — pose, déplacement, moyens d'accès et SAV précisés dans le devis.
            </p>
            <ul className="grid sm:grid-cols-2 gap-2 pt-2 text-xs text-zinc-400">
              <li>✓ Croix pharmacie LED programmable</li>
              <li>✓ Enseigne drapeau pharmacie double face</li>
              <li>✓ Lettres boîtiers aluminium & inox</li>
              <li>✓ Caducée lumineux pharmacie</li>
              <li>✓ Logo pharmacie rétroéclairé sur mesure</li>
              <li>✓ Rénovation façade pharmacie</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-60"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase leading-tight">
              <span className="chrome-text">Un projet d'enseigne ?</span>
              <br />
              <span className="neon-text-soft">Parlons-en.</span>
            </h2>
            <p className="mt-4 text-zinc-300 font-body max-w-xl">
              {t.hero.subtitle}
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="btn-neon" data-testid="cta-band-contact">
              {t.nav.quote}
            </Link>
            <Link to="/produits" className="btn-ghost-neon" data-testid="cta-band-products">
              {t.nav.products}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
